import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, XCircle, KeyRound, RefreshCw, Link as LinkIcon, ExternalLink, MessageCircle, Copy } from "lucide-react";

export default function AdminSettings() {
  const { user } = useAuth();
  const [integrations, setIntegrations] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const loadStatus = () => api.get("/admin/square/status").then((r) => setSyncStatus(r.data));
  const loadWa = () => api.get("/admin/whatsapp/status").then((r) => setWaStatus(r.data));

  const [waStatus, setWaStatus] = useState(null);
  const [palletCfg, setPalletCfg] = useState(null);
  const [savingPallet, setSavingPallet] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);
  const [newBlock, setNewBlock] = useState({ date: "", reason: "" });
  const [reviewCfg, setReviewCfg] = useState(null);
  const [savingReview, setSavingReview] = useState(false);

  const loadReview = () =>
    api.get("/admin/review-settings").then((r) => setReviewCfg(r.data)).catch(() => {});

  const saveReview = async () => {
    setSavingReview(true);
    try {
      const { data } = await api.put("/admin/review-settings", {
        google_review_url: reviewCfg.google_review_url || "",
        delay_hours: parseFloat(reviewCfg.delay_hours) || 0,
      });
      toast.success(data.enabled ? "Review requests are ON" : "Saved — review requests stay OFF until you paste a link");
      loadReview();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSavingReview(false);
    }
  };

  const loadBlocked = () =>
    api.get("/delivery/blocked-dates").then((r) => setBlockedDates(r.data.items || [])).catch(() => {});

  useEffect(() => {
    api.get("/admin/integrations").then((r) => setIntegrations(r.data));
    api.get("/pallet-builder").then((r) => setPalletCfg(r.data));
    loadStatus();
    loadWa();
    loadBlocked();
    loadReview();
  }, []);

  const addBlockedDate = async () => {
    if (!newBlock.date) return toast.error("Pick a date first");
    try {
      await api.post("/delivery/blocked-dates", newBlock);
      setNewBlock({ date: "", reason: "" });
      loadBlocked();
      toast.success("Date blocked — customers can no longer pick it");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to block date");
    }
  };

  const removeBlockedDate = async (date) => {
    try {
      await api.delete(`/delivery/blocked-dates/${date}`);
      loadBlocked();
      toast.success("Date unblocked");
    } catch {
      toast.error("Failed to unblock");
    }
  };

  const savePallet = async () => {
    setSavingPallet(true);
    try {
      const clean = {
        ...palletCfg,
        tiers: palletCfg.tiers
          .map((t) => ({ item_count: parseInt(t.item_count) || 0, price: parseFloat(t.price) || 0 }))
          .filter((t) => t.item_count >= 2 && t.price > 0),
      };
      const r = await api.put("/pallet-builder/admin", clean);
      setPalletCfg(r.data);
      toast.success("Pallet builder saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSavingPallet(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  const runSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/square/sync");
      if (data.ok) {
        toast.success(
          `Sync complete · ${data.created} created · ${data.updated} updated · ${data.error_count} errors`
        );
      } else {
        toast.error(data.error || "Sync failed");
      }
      loadStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const onChangePassword = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    if (form.new_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-settings" className="max-w-2xl">
      <div className="ipv-eyebrow text-[#0A1C40]">Settings</div>
      <h1 className="font-display font-black text-3xl tracking-tighter mt-2">Account & integrations</h1>

      <section className="bg-white border border-gray-200 p-6 mt-8">
        <div className="ipv-eyebrow mb-4 flex items-center gap-2">
          <KeyRound className="w-4 h-4" /> Change password
        </div>
        <div className="text-xs text-gray-500 mb-4">
          Signed in as <b>{user?.email}</b>
        </div>
        <form onSubmit={onChangePassword} className="space-y-3">
          <input
            type="password"
            placeholder="Current password"
            className="input-base"
            value={form.current_password}
            onChange={(e) => setForm({ ...form, current_password: e.target.value })}
            required
            data-testid="cp-current"
          />
          <input
            type="password"
            placeholder="New password (min 6 characters)"
            className="input-base"
            value={form.new_password}
            onChange={(e) => setForm({ ...form, new_password: e.target.value })}
            required
            data-testid="cp-new"
          />
          <input
            type="password"
            placeholder="Confirm new password"
            className="input-base"
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            required
            data-testid="cp-confirm"
          />
          <button type="submit" disabled={saving} className="btn-primary" data-testid="cp-submit">
            {saving ? "Saving…" : "Update password"}
          </button>
        </form>
      </section>

      <section className="bg-white border border-gray-200 p-6 mt-6" data-testid="pallet-builder-section">
        <div className="ipv-eyebrow mb-4">Build-a-Pallet pricing</div>
        {palletCfg && (
          <>
            <label className="flex items-center gap-2 text-sm mb-4">
              <input
                type="checkbox"
                checked={palletCfg.enabled}
                onChange={(e) => setPalletCfg({ ...palletCfg, enabled: e.target.checked })}
                data-testid="pallet-enabled-toggle"
              />
              Enable "Build a Pallet" on the store
            </label>
            <div className="space-y-2">
              {palletCfg.tiers.map((t, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <input
                    type="number" min="2" className="input-base !w-24"
                    value={t.item_count}
                    onChange={(e) => setPalletCfg({ ...palletCfg, tiers: palletCfg.tiers.map((x, xi) => xi === i ? { ...x, item_count: e.target.value } : x) })}
                    data-testid={`pallet-tier-count-${i}`}
                  />
                  <span>items for $</span>
                  <input
                    type="number" min="1" step="0.01" className="input-base !w-28"
                    value={t.price}
                    onChange={(e) => setPalletCfg({ ...palletCfg, tiers: palletCfg.tiers.map((x, xi) => xi === i ? { ...x, price: e.target.value } : x) })}
                    data-testid={`pallet-tier-price-${i}`}
                  />
                  <button type="button" className="text-gray-400 hover:text-[#DC2626] text-xs font-bold" onClick={() => setPalletCfg({ ...palletCfg, tiers: palletCfg.tiers.filter((_, xi) => xi !== i) })} data-testid={`pallet-tier-remove-${i}`}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-4">
              <button type="button" className="btn-outline text-sm" onClick={() => setPalletCfg({ ...palletCfg, tiers: [...palletCfg.tiers, { item_count: 5, price: 199 }] })} data-testid="pallet-tier-add">
                + Add tier
              </button>
              <button type="button" className="btn-primary text-sm" onClick={savePallet} disabled={savingPallet} data-testid="pallet-save-btn">
                {savingPallet ? "Saving…" : "Save pallet pricing"}
              </button>
            </div>
          </>
        )}
      </section>

      <section className="bg-white border border-gray-200 p-6 mt-6" data-testid="delivery-schedule-section">
        <div className="ipv-eyebrow mb-2">Delivery Schedule</div>
        <p className="text-xs text-gray-600 mb-4">
          Deliveries run <b>Tuesday · Thursday · Saturday</b> (Saturday window 9 AM–1 PM). Customers must order by{" "}
          <b>2 PM the day before</b>. Block a date below (holiday, van full) and it instantly disappears from checkout.
        </p>
        <div className="flex flex-wrap gap-2 items-center">
          <input
            type="date"
            className="input-base max-w-[180px]"
            value={newBlock.date}
            onChange={(e) => setNewBlock({ ...newBlock, date: e.target.value })}
            data-testid="block-date-input"
          />
          <input
            className="input-base flex-1 min-w-[160px]"
            placeholder="Reason (optional)"
            value={newBlock.reason}
            onChange={(e) => setNewBlock({ ...newBlock, reason: e.target.value })}
            data-testid="block-reason-input"
          />
          <button type="button" className="btn-outline text-sm" onClick={addBlockedDate} data-testid="block-date-btn">
            Block date
          </button>
        </div>
        {blockedDates.length > 0 ? (
          <div className="mt-4 space-y-1">
            {blockedDates.map((b) => (
              <div key={b.date} className="flex items-center justify-between text-sm border border-gray-200 px-3 py-2" data-testid={`blocked-date-${b.date}`}>
                <span>
                  <b>{b.date}</b>
                  {b.reason ? <span className="text-gray-500"> — {b.reason}</span> : null}
                </span>
                <button type="button" className="text-red-600 text-xs hover:underline" onClick={() => removeBlockedDate(b.date)} data-testid={`unblock-${b.date}`}>
                  Unblock
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-3 text-xs text-gray-400" data-testid="no-blocked-dates">No blocked dates.</div>
        )}
      </section>

      <section className="bg-white border border-gray-200 p-6 mt-6" data-testid="review-requests-section">
        <div className="ipv-eyebrow mb-2 flex items-center justify-between">
          <span>Google Review Requests</span>
          {reviewCfg?.enabled ? (
            <span className="text-[10px] font-bold bg-green-100 text-green-700 px-2 py-1">ACTIVE</span>
          ) : (
            <span className="text-[10px] font-bold bg-gray-100 text-gray-600 px-2 py-1">OFF — no link yet</span>
          )}
        </div>
        <p className="text-xs text-gray-600 mb-4">
          After an order is completed/delivered, we wait the delay below then text + email the customer your Google
          review link. <b>Nothing sends until you paste a real link</b> (no fallback). Get it from your Google Business
          Profile → "Ask for reviews".
        </p>
        {reviewCfg && (
          <>
            <label className="ipv-eyebrow block mb-1.5">Google review link</label>
            <input
              className="input-base"
              placeholder="https://g.page/r/…/review"
              value={reviewCfg.google_review_url || ""}
              onChange={(e) => setReviewCfg({ ...reviewCfg, google_review_url: e.target.value })}
              data-testid="review-url-input"
            />
            <label className="ipv-eyebrow block mb-1.5 mt-4">Delay after delivery (hours)</label>
            <input
              type="number" step="0.5" min="0" max="72"
              className="input-base max-w-[120px]"
              value={reviewCfg.delay_hours}
              onChange={(e) => setReviewCfg({ ...reviewCfg, delay_hours: e.target.value })}
              data-testid="review-delay-input"
            />
            <div className="flex items-center gap-4 mt-4">
              <button className="btn-primary text-sm" onClick={saveReview} disabled={savingReview} data-testid="review-save-btn">
                {savingReview ? "Saving…" : "Save review settings"}
              </button>
              <span className="text-xs text-gray-500">Queue: {reviewCfg.pending || 0} pending · {reviewCfg.sent || 0} sent</span>
            </div>
          </>
        )}
      </section>

      <section className="bg-white border border-gray-200 p-6 mt-6" data-testid="data-fix-section">
        <div className="ipv-eyebrow mb-2">One-Time Cleanup — Pallet Flags & Categories</div>
        <p className="text-xs text-gray-600 mb-3">
          Fixes retail items (shampoo, lotion, lighters…) that were wrongly flagged as pallets and were charging
          $75/$99 pallet delivery. Real pallets (name contains "pallet" or has a manifest) keep the pallet rate;
          everything else moves to Health &amp; Beauty / Household / General Merchandise with standard delivery tiers.
          Safe to run more than once.
        </p>
        <button
          className="btn-outline text-sm"
          data-testid="run-pallet-fix-btn"
          onClick={async () => {
            try {
              const { data } = await api.post("/admin/data-fix/pallet-flags");
              toast.success(`Fixed ${data.retail_items_fixed} retail items · kept ${data.real_pallets_kept} real pallets`);
            } catch (e) {
              toast.error(e.response?.data?.detail || "Cleanup failed");
            }
          }}
        >
          Fix pallet flags &amp; categories now
        </button>
      </section>

      <section className="bg-white border border-gray-200 p-6 mt-6" data-testid="whatsapp-section">
        <div className="ipv-eyebrow mb-4 flex items-center justify-between">
          <span className="flex items-center gap-2"><MessageCircle className="w-4 h-4" /> WhatsApp Catalog Inbox</span>
          {waStatus?.configured?.fully_ready ? (
            <span className="text-[10px] font-bold uppercase tracking-widest bg-green-100 text-green-700 px-2 py-1 normal-case tracking-normal">Active</span>
          ) : (
            <span className="text-[10px] font-bold uppercase tracking-widest bg-yellow-100 text-yellow-700 px-2 py-1 normal-case tracking-normal">Setup needed</span>
          )}
        </div>

        {!waStatus ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : (
          <div className="space-y-5 text-sm">
            <p className="text-gray-700">
              Forward product photos + price from your WhatsApp to your dedicated Twilio number — products go live on the website automatically with auto-generated SKUs.
            </p>

            <div className="bg-amber-50 border border-amber-200 p-3 text-xs" data-testid="wa-manifest-tip">
              <div className="font-bold text-amber-900 mb-1">📋 Attach a manifest from your phone</div>
              <div className="text-amber-900/90">
                Send a flyer/manifest photo with caption <code className="bg-white border border-amber-200 px-1">manifest &lt;SKU&gt;</code> (e.g. <code className="bg-white border border-amber-200 px-1">manifest WA-AB12CD</code>) and the line items + total MSRP get auto-attached to that pallet.
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 text-xs">
              {[
                { k: "account_sid", l: "Account SID" },
                { k: "auth_token", l: "Auth Token" },
                { k: "api_key", l: "API Key" },
                { k: "whatsapp_from", l: "Twilio WA #" },
                { k: "owner_number", l: "Owner #" },
                { k: "fully_ready", l: "Ready to go" },
              ].map((x) => (
                <div key={x.k} className={`border p-2 flex items-center gap-2 ${waStatus.configured[x.k] ? "border-green-300 bg-green-50" : "border-gray-200 bg-gray-50"}`}>
                  {waStatus.configured[x.k] ? <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" /> : <XCircle className="w-4 h-4 text-gray-300 shrink-0" />}
                  <span className={waStatus.configured[x.k] ? "font-bold" : ""}>{x.l}</span>
                </div>
              ))}
            </div>

            <div className="bg-[#FAFAFA] border border-gray-200 p-3 text-xs">
              <div className="ipv-eyebrow mb-1">Webhook URL (paste into Twilio Sandbox config)</div>
              <div className="flex items-center gap-2">
                <code className="bg-white border border-gray-200 px-2 py-1 flex-1 truncate text-[11px]">{waStatus.webhook_url}</code>
                <button onClick={() => copy(waStatus.webhook_url)} className="btn-outline !px-2 !py-1" data-testid="copy-webhook-url">
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            <div>
              <div className="font-bold mb-2 text-sm">📋 Setup checklist (5 min)</div>
              <ol className="list-decimal list-inside space-y-1.5 text-xs text-gray-700">
                <li>
                  In Twilio Console → <a href="https://console.twilio.com" target="_blank" rel="noreferrer" className="text-[#0A1C40] hover:underline">Account Info</a>, copy your <b>Account SID</b> (starts with <code>AC...</code>) and <b>Auth Token</b>. Send them to me to paste in backend/.env.
                </li>
                <li>
                  Go to <a href="https://console.twilio.com/us1/develop/sms/try-it-out/whatsapp-learn" target="_blank" rel="noreferrer" className="text-[#0A1C40] hover:underline">Messaging → Try it out → WhatsApp</a>. Note your sandbox <b>join code</b> (e.g., "join purple-river").
                </li>
                <li>
                  On your phone, WhatsApp <code>+1 415 523 8886</code> and send "<b>join &lt;your-code&gt;</b>". Wait for confirmation reply.
                </li>
                <li>
                  Back in Twilio sandbox settings, paste this webhook URL into <b>"When a message comes in"</b>: <code className="bg-gray-100 px-1 break-all">{waStatus.webhook_url}</code> (HTTP POST). Save.
                </li>
                <li>
                  Send me your <b>WhatsApp number</b> (the one you joined from, format <code>+1XXXXXXXXXX</code>) — I'll set it as the allowlisted owner so only you can add products.
                </li>
              </ol>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="border border-gray-200 p-3">
                <div className="font-display font-black text-xl">{waStatus.products_via_whatsapp}</div>
                <div className="ipv-eyebrow text-gray-500 mt-1">Added via WhatsApp</div>
              </div>
              <div className="border border-gray-200 p-3">
                <div className="font-display font-black text-xl">{waStatus.recent_messages?.length || 0}</div>
                <div className="ipv-eyebrow text-gray-500 mt-1">Recent inbound (last 10)</div>
              </div>
            </div>

            {waStatus.recent_messages?.length > 0 && (
              <div className="border border-gray-200">
                <div className="ipv-eyebrow bg-[#FAFAFA] px-3 py-2 border-b border-gray-200">Recent messages</div>
                <div className="divide-y divide-gray-100 text-xs max-h-40 overflow-auto">
                  {waStatus.recent_messages.map((m, i) => (
                    <div key={i} className="px-3 py-2 flex items-center justify-between gap-2">
                      <div className="truncate">
                        <b>{m.from}</b> — {m.body || "(no caption)"} · {m.num_media} 📷
                      </div>
                      <div className="text-gray-400">{new Date(m.received_at).toLocaleTimeString()}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button onClick={loadWa} className="btn-outline inline-flex items-center gap-2 text-xs" data-testid="wa-refresh-btn">
              <RefreshCw className="w-3 h-3" /> Refresh status
            </button>
          </div>
        )}
      </section>

      <section className="bg-white border border-gray-200 p-6 mt-6" data-testid="square-sync-section">
        <div className="ipv-eyebrow mb-4 flex items-center justify-between">
          <span className="flex items-center gap-2"><LinkIcon className="w-4 h-4" /> Square Catalog Sync</span>
          <a href="https://app.squareup.com/dashboard/items/library" target="_blank" rel="noreferrer" className="normal-case tracking-normal text-xs text-[#0A1C40] hover:underline inline-flex items-center gap-1">
            Square Item Library <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        {!syncStatus ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-3 gap-3">
              <div className="border border-gray-200 p-3">
                <div className="font-display font-black text-2xl">{syncStatus.linked_products}</div>
                <div className="ipv-eyebrow text-gray-500 mt-1">Linked</div>
              </div>
              <div className="border border-gray-200 p-3">
                <div className="font-display font-black text-2xl">{syncStatus.total_products}</div>
                <div className="ipv-eyebrow text-gray-500 mt-1">Total products</div>
              </div>
              <div className="border border-gray-200 p-3">
                <div className={`font-display font-black text-2xl ${syncStatus.webhook_configured ? "text-green-700" : "text-gray-300"}`}>
                  {syncStatus.webhook_configured ? "On" : "Off"}
                </div>
                <div className="ipv-eyebrow text-gray-500 mt-1">Webhooks</div>
              </div>
            </div>

            {syncStatus.last_sync ? (
              <div className="bg-[#FAFAFA] border border-gray-200 p-3 text-xs space-y-1">
                <div><b>Last sync:</b> {new Date(syncStatus.last_sync.started_at).toLocaleString()}</div>
                <div className="text-gray-600">
                  Created {syncStatus.last_sync.created || 0} ·
                  Updated {syncStatus.last_sync.updated || 0} ·
                  Skipped (no SKU) {syncStatus.last_sync.skipped_no_sku || 0} ·
                  Errors {syncStatus.last_sync.error_count || 0}
                </div>
              </div>
            ) : (
              <div className="text-xs text-gray-500">No sync runs yet.</div>
            )}

            <div className="flex flex-wrap gap-3">
              <button onClick={runSync} disabled={syncing} className="btn-primary inline-flex items-center gap-2" data-testid="sync-now-btn">
                <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
                {syncing ? "Syncing…" : "Sync now from Square"}
              </button>
            </div>

            <div className="border-t border-gray-200 pt-4 text-xs text-gray-600 space-y-2">
              <div className="font-bold text-gray-700 uppercase tracking-wider text-[10px]">How sync works</div>
              <ul className="list-disc list-inside space-y-1">
                <li>Pulls every item from your Square Item Library (matched by <b>SKU</b>) and upserts to the website</li>
                <li>Runs automatically every 15 minutes in the background</li>
                <li>Editing a linked product here pushes the new price/name back to Square instantly</li>
                <li>To enable real-time webhooks: add a webhook subscription in Square Developer Dashboard pointing to <code className="bg-gray-100 px-1">{(syncStatus.webhook_url_hint?.startsWith('http') ? syncStatus.webhook_url_hint : 'https://your-domain/api/webhooks/square')}</code>, then paste the signature key as <code className="bg-gray-100 px-1">SQUARE_WEBHOOK_SIGNATURE_KEY</code> in backend/.env</li>
              </ul>
            </div>
          </div>
        )}
      </section>

      <section className="bg-white border border-gray-200 p-6 mt-6">
        <div className="ipv-eyebrow mb-4">Integrations</div>
        {!integrations ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : (
          <div className="space-y-3 text-sm">
            <IntegrationRow
              name="Square Payments"
              configured={true}
              detail="Production mode — accepting card payments"
            />
            <IntegrationRow
              name={`SMS (${integrations.sms.provider})`}
              configured={integrations.sms.configured}
              detail={
                integrations.sms.configured
                  ? "SMS alerts active for order status changes"
                  : "Add TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_PHONE_NUMBER to backend/.env to enable"
              }
            />
            <IntegrationRow
              name={`Email (${integrations.email.provider})`}
              configured={integrations.email.configured}
              detail={
                integrations.email.configured
                  ? "Order confirmation emails active"
                  : "Add RESEND_API_KEY to backend/.env (and verify a sender domain at resend.com) to enable"
              }
            />
          </div>
        )}
      </section>
    </div>
  );
}

function IntegrationRow({ name, configured, detail }) {
  return (
    <div className="flex items-start gap-3 py-2">
      {configured ? (
        <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
      ) : (
        <XCircle className="w-5 h-5 text-gray-300 mt-0.5 shrink-0" />
      )}
      <div className="flex-1">
        <div className="font-bold">{name}</div>
        <div className="text-xs text-gray-600 mt-0.5">{detail}</div>
      </div>
      <span
        className={`text-[10px] tracking-widest font-bold uppercase px-2 py-1 ${
          configured ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
        }`}
      >
        {configured ? "Active" : "Inactive"}
      </span>
    </div>
  );
}
